import { useState, useEffect, useRef } from 'react';

import { useAuthContext } from '@/context/AuthContext';
import { useSeasonResults } from '@/hooks/usePointsLottery';

/**
 * Storage key for tracking which seasons the user has seen the summary for
 * Uses season_no for consistency
 * Format: junkfun_season_summary_seen_season{seasonNo}
 */
const getStorageKey = (seasonNo: number) => `junkfun_season_summary_seen_season${seasonNo}`;

/**
 * List of all seasons to check (in order)
 * Uses season_no (1, 2, 3...) which is the consistent identifier
 * The backend API now accepts season_no directly, not database id
 */
const SEASONS_TO_CHECK = [1, 2]; // Season 1, Season 2

/**
 * Hook to manage the Season Summary modal display logic for MULTIPLE seasons
 *
 * Checks each season in order and shows the first one where:
 * 1. User participated (has activity)
 * 2. User hasn't seen the summary yet
 *
 * This allows users to mint NFTs from previous seasons they missed
 */
export function useMultiSeasonSummary() {
  const [showSummary, setShowSummary] = useState(false);
  const [activeSeasonNo, setActiveSeasonNo] = useState<number | null>(null);
  const [hasChecked, setHasChecked] = useState(false);
  const { isAuthenticated } = useAuthContext();

  // Use ref to track the timeout so it persists across re-renders
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const modalScheduledRef = useRef(false);

  // Fetch results for all seasons using season_no directly
  const season1Results = useSeasonResults(1, isAuthenticated); // Season 1
  const season2Results = useSeasonResults(2, isAuthenticated); // Season 2

  // Check all seasons and determine which one to show
  useEffect(() => {
    // Don't check until both season results are loaded
    if (season1Results.isLoading || season2Results.isLoading) {
      return;
    }

    // Only check once per session, and don't re-check if modal is already scheduled
    if (hasChecked || !isAuthenticated || modalScheduledRef.current) {
      return;
    }

    setHasChecked(true);

    // Debug logging
    console.log('[useMultiSeasonSummary] Checking seasons:', {
      season1: {
        hasResult: !!season1Results.data?.userResult,
        points: season1Results.data?.userResult?.pointsContributed,
        seen: localStorage.getItem(getStorageKey(1)) === 'true',
      },
      season2: {
        hasResult: !!season2Results.data?.userResult,
        points: season2Results.data?.userResult?.pointsContributed,
        seen: localStorage.getItem(getStorageKey(2)) === 'true',
      },
    });

    // Check each season in order
    const seasonsToCheck = [
      { seasonNo: 1, results: season1Results.data },
      { seasonNo: 2, results: season2Results.data },
    ];

    for (const { seasonNo, results } of seasonsToCheck) {
      const seen = localStorage.getItem(getStorageKey(seasonNo)) === 'true';

      if (seen) {
        console.log(`[useMultiSeasonSummary] Season ${seasonNo} already seen, skipping`);
        continue;
      }

      if (!results?.userResult) {
        console.log(`[useMultiSeasonSummary] Season ${seasonNo} has no user result, skipping`);
        continue;
      }

      const hasActivity =
        results.userResult.pointsContributed &&
        Number(results.userResult.pointsContributed) > 0;

      if (!hasActivity) {
        console.log(`[useMultiSeasonSummary] Season ${seasonNo} has no activity, skipping`);
        continue;
      }

      // Found a season to show!
      console.log(`[useMultiSeasonSummary] Will show Season ${seasonNo} summary`);
      setActiveSeasonNo(seasonNo);
      modalScheduledRef.current = true;

      // Small delay to let the page load first
      // Store in ref so it persists across re-renders
      timerRef.current = setTimeout(() => {
        console.log(`[useMultiSeasonSummary] Showing Season ${seasonNo} modal now`);
        setShowSummary(true);
      }, 1500);

      break; // Stop checking after finding the first season to show
    }

    if (!modalScheduledRef.current) {
      console.log('[useMultiSeasonSummary] No unseen seasons with activity found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    hasChecked,
    season1Results.isLoading,
    season2Results.isLoading,
    // Remove data from dependencies to prevent timeout cancellation on re-render
    // We only need to check once when loading completes
  ]);

  // Cleanup timeout on unmount only
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /**
   * Mark the current season's summary as seen and close the modal
   */
  const markAsSeen = () => {
    if (activeSeasonNo !== null) {
      const storageKey = getStorageKey(activeSeasonNo);
      localStorage.setItem(storageKey, 'true');
      console.log(`[useMultiSeasonSummary] Marked Season ${activeSeasonNo} as seen`);
    }
    setShowSummary(false);
    setActiveSeasonNo(null);
    // Reset to allow checking for next season
    setHasChecked(false);
  };

  /**
   * Close the modal without marking as seen
   * (User can close it but it will show again next time)
   */
  const closeSummary = () => {
    setShowSummary(false);
  };

  /**
   * Reset all "seen" states for testing purposes
   */
  const resetAllSummaries = () => {
    SEASONS_TO_CHECK.forEach((seasonNo) => {
      const storageKey = getStorageKey(seasonNo);
      localStorage.removeItem(storageKey);
    });
    setHasChecked(false);
    setActiveSeasonNo(null);
    modalScheduledRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    showSummary,
    seasonId: activeSeasonNo, // Now uses season_no consistently (1, 2, 3...)
    seasonNo: activeSeasonNo, // Same as seasonId for clarity
    markAsSeen,
    closeSummary,
    resetAllSummaries,
  };
}

